/**
 * Shared helpers for space closed-period boundaries.
 *
 * Formats:
 * - One-off: YYYY-MM-DD or YYYY-MM-DDTHH:MM
 * - Recurring: MM-DD or MM-DDTHH:MM
 * Date-only end is inclusive of the whole last day.
 */

export const WEEKDAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const ALL_DAY_SCHEDULE_DAYS = WEEKDAY_NAMES.map(day => ({
  day,
  time_start: '00:00',
  time_end: '23:59',
}));

const DATETIME_RE =
  /^(\d{4}-\d{2}-\d{2}|(\d{4}-)?\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?$/;

export const parseClosedPeriodBoundary = (
  value,
  { recurring = false } = {}
) => {
  if (!value) return { date: '', time: '' };
  const match = String(value).trim().match(DATETIME_RE);
  if (!match) {
    return { date: String(value), time: '' };
  }
  let datePart = match[1];
  const timePart = match[3] || '';
  if (recurring) {
    datePart = datePart.slice(-5);
  }
  return { date: datePart, time: timePart };
};

export const serializeClosedPeriodBoundary = (
  datePart,
  timePart,
  { recurring = false } = {}
) => {
  if (!datePart) return '';
  let date = String(datePart).trim();
  if (recurring) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      date = date.slice(-5);
    }
  }
  const time = timePart ? String(timePart).trim().slice(0, 5) : '';
  return time ? `${date}T${time}` : date;
};

export const normalizeRecurringClosedPeriodBoundary = value => {
  if (!value) return value;
  const { date, time } = parseClosedPeriodBoundary(value, { recurring: true });
  return serializeClosedPeriodBoundary(date, time, { recurring: true });
};

export const emptyClosedPeriodForm = (overrides = {}) => ({
  title: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  is_recurring: false,
  ...overrides,
});

export const closedPeriodFromStorage = period => {
  const recurring = !!period?.is_recurring;
  const start = parseClosedPeriodBoundary(period?.start, { recurring });
  const end = parseClosedPeriodBoundary(period?.end, { recurring });
  return {
    title: period?.title || '',
    startDate: start.date,
    endDate: end.date,
    startTime: start.time,
    endTime: end.time,
    is_recurring: recurring,
  };
};

export const closedPeriodToStorage = form => {
  const recurring = !!form.is_recurring;
  return {
    title: form.title || '',
    start: serializeClosedPeriodBoundary(form.startDate, form.startTime, {
      recurring,
    }),
    end: serializeClosedPeriodBoundary(form.endDate, form.endTime, {
      recurring,
    }),
    is_recurring: recurring,
  };
};

export const formatDateInput = date => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** One-off periods whose end is before now. Recurring yearly periods are never past. */
export const isPastClosedPeriodForm = (form, now = new Date()) => {
  if (!form || form.is_recurring) return false;
  if (!form.endDate || !/^\d{4}-\d{2}-\d{2}$/.test(form.endDate)) return false;

  const [year, month, day] = form.endDate.split('-').map(Number);
  let endAt;
  if (form.endTime && /^\d{2}:\d{2}/.test(form.endTime)) {
    const [hour, minute] = form.endTime.split(':').map(Number);
    endAt = new Date(year, month - 1, day, hour, minute, 0, 0);
  } else {
    // Date-only end is inclusive of that whole day.
    endAt = new Date(year, month - 1, day + 1, 0, 0, 0, 0);
  }
  return endAt.getTime() <= now.getTime();
};
